/**
 * Every entity in the system. These types are shared by the front desk (Dexie)
 * and the portal (Supabase) so the two can never disagree about shape.
 *
 * Two conventions that are load-bearing:
 *
 *  1. Money is ALWAYS an integer number of piastres (1 EGP = 100 piastres).
 *     There are no floats anywhere near currency. Fields are suffixed
 *     `_piastres` so a bare `price` can never be mistaken for pounds.
 *
 *  2. Dates that represent a calendar day are `YYYY-MM-DD` strings, never
 *     timestamps. A subscription that ends "12 Sep" ends on 12 Sep in Cairo
 *     regardless of the browser's timezone. Only audit fields
 *     (`created_at` etc.) are true instants.
 */

/** A calendar day, `YYYY-MM-DD`. Never a timestamp. */
export type ISODate = string;

/** An instant, ISO-8601 UTC, e.g. `2026-08-02T18:30:00.000Z`. */
export type ISOTimestamp = string;

/**
 * IndexedDB cannot index boolean values, so any flag we need to *query* is
 * stored as 0 | 1. `dirty` is queried on every sync push, hence this type.
 */
export type Flag = 0 | 1;

/** Fields carried by every synced table. */
export interface BaseRow {
  /** UUID v4, generated client-side with crypto.randomUUID(). */
  id: string;
  created_at: ISOTimestamp;
  updated_at: ISOTimestamp;
  /** Soft delete. Rows are never removed; every query filters this to null. */
  deleted_at: ISOTimestamp | null;
  /** Local only — never sent to Supabase. 1 = has unpushed local changes. */
  dirty: Flag;
}

export interface Member extends BaseRow {
  /** Human-readable, `SK-0001`. Unique. Only the front desk device mints these. */
  sk_id: string;
  full_name: string;
  /** Digits only. Duplicates are allowed but warned about. */
  phone: string;
  /**
   * Club house members pay the reduced rate on the price schedule. This is a
   * property of the person, not of a subscription — they either hold a club
   * house membership or they do not.
   */
  club_member: boolean;
  notes: string;
}

export interface Subscription extends BaseRow {
  member_id: string;
  class_key: string;
  /** One-to-one training costs more. Chosen per subscription, not per member. */
  private: boolean;
  /**
   * Who teaches the one-to-one sessions, chosen at the point of sale.
   *
   * Empty for group subscriptions. It is deliberately NOT the class coach: the
   * person running a private hour is frequently not the one running the class,
   * and the payout report attributes private revenue to whoever is named here.
   *
   * Stored as a NAME rather than a coach id, and snapshotted like the price —
   * renaming a coach in Settings must not silently re-attribute money that was
   * already paid out.
   */
  private_coach: string;
  /**
   * Copied from the member at the time of sale. If someone later joins the
   * club house, past subscriptions must still show the rate they actually paid.
   */
  club_member: boolean;
  start_date: ISODate;
  /** Only 1, 3 or 6 are sold. */
  months: number;
  /** Computed from start_date + months, then stored so history is immutable. */
  end_date: ISODate;
  /**
   * The TOTAL price for the whole term at the time of sale — NOT per month.
   * The schedule has bulk discounts baked in (3 months costs less than 3 × 1
   * month), so there is no monthly rate to multiply. Editing the schedule later
   * must never change what a past subscription cost; this snapshot is why.
   */
  price_piastres: number;
  discount_piastres: number;
  /** price − discount. Stored, not recomputed on read. */
  total_due_piastres: number;
  cancelled: boolean;
  /**
   * The day the member cancelled. Null while the subscription is live.
   *
   * Kept alongside `cancelled` rather than replacing it because status is
   * derived from the flag and the portals already read it; this is the audit
   * trail for WHEN, which a refund argument three months later needs.
   */
  cancelled_at: ISODate | null;
  /**
   * Sessions the member had already attended when they cancelled, entered by
   * the front desk. Null unless cancelled.
   */
  sessions_attended: number | null;
  /**
   * What was handed back, as a positive number. Null unless cancelled.
   *
   * This is a record of the decision. The MONEY moves via a negative row in
   * `payments`, which is what makes the refund show up in every revenue figure
   * without any report knowing cancellations exist.
   */
  refund_piastres: number | null;
}

/**
 * Append-only. Payments are never edited or deleted — a correction is a new
 * row, possibly negative. This is why money never conflicts during sync.
 */
export interface Payment extends BaseRow {
  subscription_id: string;
  member_id: string;
  amount_piastres: number;
  method: PaymentMethod;
  paid_at: ISODate;
  received_by: string;
}

export const PAYMENT_METHODS = ['cash', 'instapay', 'card'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Cash',
  instapay: 'InstaPay',
  card: 'Card',
};

/**
 * One cell of the price schedule, e.g. `adults_private_club_member_3m`.
 * 24 rows: 2 age groups × group/private × member/non-member × 1/3/6 months.
 */
export interface PriceRow extends BaseRow {
  price_key: string;
  price_piastres: number;
}

/** Who teaches a class. Drives the 40% coach share in the payout report. */
export interface CoachAssignment extends BaseRow {
  class_key: string;
  coach_name: string;
}

/**
 * A coach on the roster who is NOT tied to a class.
 *
 * Class coaches are implied by `coach_assignments`; this table exists for the
 * people who only run private sessions, who would otherwise be unnameable —
 * there is no class to assign them to. Both sets together are what the private
 * coach dropdown offers, and both appear in the payout report even at zero, so
 * a coach who did no privates this month reads as "earned nothing" rather than
 * vanishing.
 */
export interface Coach extends BaseRow {
  name: string;
}

/**
 * A paused stretch of a subscription.
 *
 * Creating one pushes the subscription's `end_date` out by `days`, so every
 * existing query — status, renewals, the dashboard — keeps working without
 * knowing freezes exist. These rows are the audit trail for how that date got
 * where it is, and cancelling one pulls the date back again.
 *
 * Dates are inclusive at both ends: a freeze from the 10th for 1 day starts
 * and ends on the 10th.
 */
export interface Freeze extends BaseRow {
  subscription_id: string;
  member_id: string;
  start_date: ISODate;
  end_date: ISODate;
  days: number;
  reason: string;
  created_by: string;
}

/**
 * A single drop-in session, one flat price for any class.
 *
 * Deliberately NOT a member with a one-day subscription: walk-ins are often
 * one-time visitors, and minting an SK ID for each would fill the member list
 * with people who never come back.
 *
 * Name and phone ARE required — a drop-in is still a person on the mat, and
 * the academy needs to know who was in the building.
 *
 * Revenue reporting counts walk-ins alongside subscription payments.
 */
export interface WalkIn extends BaseRow {
  class_key: string;
  /** Required — every drop-in is recorded against a name. */
  person_name: string;
  /** Required, digits only. */
  phone: string;
  /** Snapshot of the walk-in price at the time of the visit. */
  amount_piastres: number;
  method: PaymentMethod;
  visited_at: ISODate;
  received_by: string;
}

/**
 * Key/value store. Local to this device and NOT synced: the PIN hashes, the
 * sync cursors and the clock offset are device-specific, and syncing a cursor
 * would corrupt it.
 */
export interface Setting {
  key: string;
  value: string;
  updated_at: ISOTimestamp;
}

/**
 * Shared configuration that DOES sync: the walk-in price, the payout split,
 * the gym name. Separate from `Setting` precisely so the device-specific
 * things above can never leak to the cloud.
 *
 * This is what lets a manager change a price from the portal and have the
 * front desk pick it up on the next sync.
 */
export interface AppSetting extends BaseRow {
  key: string;
  value: string;
}

/** One row per sync attempt, for debugging in Settings. Local only. */
export interface SyncLogEntry {
  seq?: number;
  started_at: ISOTimestamp;
  finished_at: ISOTimestamp | null;
  /** `offline`: the internet was unreachable. Expected at the desk, not a fault. */
  status: 'running' | 'ok' | 'error' | 'skipped' | 'offline';
  pushed: number;
  pulled: number;
  message: string;
}

/** Derived on read from dates, the cancelled flag and any live freeze. Never stored. */
export type SubscriptionStatus =
  | 'active'
  | 'expiring_soon'
  | 'expired'
  | 'cancelled'
  | 'frozen';

/** A class offered by the academy. */
export interface ClassDef {
  key: string;
  display: string;
  ageGroup: 'Adults' | 'Kids';
}

/** A subscription joined with its member and payment total, for list screens. */
export interface SubscriptionView {
  subscription: Subscription;
  member: Member;
  status: SubscriptionStatus;
  amount_paid_piastres: number;
  remaining_piastres: number;
  days_remaining: number;
}
